import { Question, SurveyModel } from "survey-core";
import { buildPayload } from "../helpers/buildPayload";
import { setProperty } from "dot-prop";
import { AxiosInstance } from "axios";
import { addressLookup } from "../helpers/addressLookup";
import { normalisePostalCode } from "../helpers/normalisePostalCode";
import { debounce } from "../helpers/debounce";
import { validatePhoneNumber } from "../helpers/validatePhoneNumber";

const syncSelectedValueVariable = (s: SurveyModel, questionName: string) => {
  const question = s.getQuestionByName(questionName);
  if (!question || question.getType() !== "dropdown") return;

  const data = (question as any).selectedItem?.data ?? null;
  s.setVariable(`${questionName}__selectedValue`, data ?? undefined);
};

const updatePageQuestionsLoadingState = (s: SurveyModel) => {
  const page = s.currentPage;
  if (!page) {
    s.setPropertyValue("isCurrentPageQuestionsLoading", false);
    return;
  }
  const isLoading = page.questions.some((q: Question) => !q.isReady);
  s.setPropertyValue("isCurrentPageQuestionsLoading", isLoading);
};

export const registerEventHandlers = (
  survey: SurveyModel,
  api: AxiosInstance,
  source: "widget" | "dealer-intake" | "mobile-app" | "website",
  {
    dataStorageKey,
    uiStateStorageKey,
    onBack,
  }: {
    dataStorageKey: string;
    uiStateStorageKey: string;
    onBack?: () => void;
  },
) => {
  survey.setVariable("activePageName", survey.activePage?.name ?? "");
  survey.setVariable("source", source);

  survey.onCurrentPageChanged.add((s) => {
    s.setVariable("activePageName", s.activePage?.name ?? "");
    s.runExpressions();

    // Copy selector address fields to personal information address fields when
    // the target page opts in via the "copySelectorAddress" schema property.
    if (s.currentPage?.getPropertyValue("copySelectorAddress")) {
      const mappings = [
        { from: "selectorPostalCode", to: "postalCode" },
        { from: "selectorStreetNumber", to: "streetNumber" },
        { from: "selectorStreetNumberAddition", to: "streetNumberAddition" },
      ] as const;

      let copied = false;
      for (const { from, to } of mappings) {
        const value = s.getValue(from);
        if (value && !s.getValue(to)) {
          s.setValue(to, value);
          copied = true;
        }
      }

      if (copied) {
        addressLookup(s);
      }
    }
  });

  const toggleNextActionVisibility = (s: SurveyModel) => {
    const hideNextAction = !!s.getVariable("hideNextAction");
    const nextAction = s.navigationBar.actions.find(
      (a) => a.id === "sv-nav-next",
    );
    if (nextAction) {
      nextAction.visible = !hideNextAction;
    }
  };

  if (onBack) {
    const prevAction = survey.navigationBar.actions.find(
      (a) => a.id === "sv-nav-prev",
    );
    if (prevAction) {
      prevAction.visible = true;
      prevAction.enabled = true;
      prevAction.action = () => {
        if (survey.currentPageNo === 0) {
          window.localStorage.removeItem(uiStateStorageKey);
          onBack();
        } else {
          survey.prevPage();
        }
      };
      survey.onCurrentPageChanged.add(() => {
        prevAction.visible = true;
        prevAction.enabled = true;
      });
    }
  }

  const debouncedAddressLookup = debounce((s: SurveyModel) => {
    addressLookup(s);
  }, 400);

  survey.onValueChanged.add((s, options) => {
    if (
      !["postalCode", "streetNumber", "streetNumberAddition"].includes(
        options.name,
      )
    )
      return;

    debouncedAddressLookup(s);
  });

  // Normalize the postal code fields (uppercase, strip whitespace) when they
  // change (on blur). `selectorPostalCode` has no address lookup, and `postalCode`
  // is only normalized by `addressLookup` when a full lookup actually runs, so both
  // are normalized here to guarantee a consistent stored value regardless of the
  // lookup path. The equality guard prevents an infinite onValueChanged loop.
  survey.onValueChanged.add((s, options) => {
    if (
      options.name !== "postalCode" &&
      options.name !== "selectorPostalCode"
    ) {
      return;
    }

    const normalised = normalisePostalCode(options.value);
    if (normalised !== options.value) {
      s.setValue(options.name, normalised);
    }
  });

  survey.onServerValidateQuestions.add((s, options) => {
    validatePhoneNumber(s, options);
  });

  survey.onValueChanged.add((s, options) => {
    toggleNextActionVisibility(s);
    window.localStorage.setItem(dataStorageKey, JSON.stringify(s.data));
    syncSelectedValueVariable(s, options.name);
  });

  survey.onVariableChanged.add((s) => {
    toggleNextActionVisibility(s);
  });

  survey.onUIStateChanged.add((s) =>
    window.localStorage.setItem(uiStateStorageKey, JSON.stringify(s.uiState)),
  );

  // Track question readiness on the current page so the next button can show a loading spinner
  // when questions are fetching choices (choicesByUrl) or doing async work (e.g. RepairAllowed)
  for (const question of survey.getAllQuestions()) {
    syncSelectedValueVariable(survey, question.name);
    question.onReadyChanged.add(() => {
      updatePageQuestionsLoadingState(survey);
      // When choices load after a data restore, sync the attached data variable
      syncSelectedValueVariable(survey, question.name);
    });

    // Set up reactive evaluation for subtitle expressions
    if (question.getPropertyValue("subtitle")) {
      question.addExpressionProperty("subtitle", (obj, result) => {
        obj.setPropertyValue("subtitleValue", result ?? "");
      });
    }
  }
  survey.onCurrentPageChanged.add((s) => {
    updatePageQuestionsLoadingState(s);
  });
  updatePageQuestionsLoadingState(survey);

  // This will only execute upon successful completion of the survey (non blocking onCompleting event handler)
  survey.onComplete.add(() => {
    window.localStorage.removeItem(dataStorageKey);
    window.localStorage.removeItem(uiStateStorageKey);
  });

  survey.onCompleting.add((s) => {
    const override = s.getVariable("thankyoubodyoverride");
    if (typeof override === "string" && override) {
      s.thankYouBody = override;
    }
  });

  survey.onCompleting.add(async (s, options) => {
    const payload = buildPayload(s);

    //TODO: Refactor backend so this is not needed
    const appointmentTimeSlot = s.getValue("appointmentTimeSlot");
    const appointmentTimeSlotPlanAt = s.getValue("appointmentTimeSlot__date");

    // Remote (e.g. Autotaalglas) slots have no plan-item id; the question stores a
    // synthetic "date+time" key instead. Only forward a real plan-item id (UUID) to the
    // backend, and rely on appointmentTimeSlotPlanAt for the concrete date and time.
    if (appointmentTimeSlotPlanAt) {
      const isPlanItemId =
        typeof appointmentTimeSlot === "string" &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          appointmentTimeSlot,
        );
      setProperty(payload, "appointmentTimeSlot", {
        appointmentTimeSlotPlanItemId: isPlanItemId
          ? appointmentTimeSlot
          : null,
        appointmentTimeSlotPlanAt,
      });
    }
    setProperty(payload, "source", source);

    const endpoint =
      s.getVariable("submitEndpoint") ?? "/dossiers/repair-direct/generic";

    s.setPropertyValue("isSubmitting", true);
    await api
      .post(endpoint, payload)
      .then((response) => {
        s.submitResponse = response.data;
      })
      .catch((error) => {
        options.allow = false;
        options.message =
          "Er is een fout opgetreden bij het versturen van het formulier. Probeer het later opnieuw.";
        console.error("Error submitting form:", error);
      })
      .finally(() => {
        s.setPropertyValue("isSubmitting", false);
      });
  });
};
